/**
 * @file Header.tsx
 * @description Header con navegación, logo y botón de logout
 * Integrado con sistema de temas para modo light/dark
 * Incluye links a Home y Favoritos con React Router
 * Muestra email del usuario y botón para cerrar sesión
 * Muestra badge de notificaciones para admins (lazy loaded)
 * @author XiaVan Team
 */

import { MapPin, LogOut, Shield } from 'lucide-react'
import { Link } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAdminAuth } from '@/hooks/useAdminAuth'

// Lazy load secondary components (only loaded when needed)
const NotificationBadge = lazy(() => import('@/components/features/NotificationBadge'))
const InstallButton = lazy(() => import('@/components/features/InstallButton'))

/**
 * Header sticky con logo de XiaVan y navegación
 * Utiliza la aplicación fullwidth sin contenedor fijo
 */
export default function Header() {
  const { user, signOut } = useAuth()
  const { isAdmin } = useAdminAuth()

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo y título */}
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="flex items-center gap-3 transition-opacity hover:opacity-80"
          >
            <img
              src="/logo.png"
              alt="XiaVan Logo"
              className="h-8 w-8 md:h-10 md:w-10"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-800 dark:text-white md:text-xl">
                XiaVan
              </h1>
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <MapPin className="h-3 w-3" />
                <span>Desde Pamplona</span>
              </div>
            </div>
          </Link>

          {/* Navegación */}
          <nav className="hidden items-center gap-6 md:flex">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 transition-colors hover:text-primary dark:text-gray-300 dark:hover:text-primary"
            >
              🏕️ Explorar
            </Link>
            <Link
              to="/favoritos"
              className="text-sm font-medium text-gray-700 transition-colors hover:text-primary dark:text-gray-300 dark:hover:text-primary"
            >
              ❤️ Favoritos
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="flex items-center gap-1.5 text-sm font-medium text-red-600 transition-colors hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <Shield className="h-4 w-4" />
                Administración
              </Link>
            )}
          </nav>
        </div>

        {/* Usuario y logout */}
        <div className="flex items-center gap-3">
          {user && (
            <>
              {/* Install PWA button (lazy loaded) */}
              <Suspense fallback={<div className="h-9 w-9" />}>
                <InstallButton />
              </Suspense>
              
              {/* Notification badge for admins (lazy loaded) */}
              <Suspense fallback={<div className="h-9 w-9" />}>
                <NotificationBadge />
              </Suspense>

              <div className="hidden flex-col items-end sm:flex">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {user.displayName || 'Usuario'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {user.email}
                </span>
              </div>
              <button
                onClick={signOut}
                className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-800 transition-colors hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                aria-label="Salir"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

